<template>
  <div class="student-email-report">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Fee Revenue Report</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ rows.length }} Batches
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" type="button" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" type="button" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem" />
      <p class="mt-3 font-weight-bold">Loading fee revenue records...</p>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" class="mt-3" @click="loadReportData">
        Retry
      </b-button>
    </div>

    <div v-else class="report-page">
      <div class="report-header-modern mb-4">
        <div class="d-flex align-items-center">
          <div v-if="schoolLogo" class="logo-wrapper mr-3">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
          </div>
          <div class="header-text-block">
            <h2 class="school-name-full mb-0">
              {{ campusName
              }}<span v-if="campusBranch" class="text-muted">
                - {{ campusBranch }}</span
              >
            </h2>
            <div class="report-info-line mt-1">
              <span class="report-name-text">Fee Revenue Report</span>
              <span class="date-range-text">
                <strong>{{ periodLabel }}</strong>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="table-container">
        <table class="ledger-style-table">
          <thead>
            <tr>
              <th class="text-center" rowspan="2">Sr#</th>
              <th class="text-left" rowspan="2">Batch Title</th>
              <th class="text-center" colspan="2">Total Students</th>
              <th
                v-for="col in feeTypeColumns"
                :key="col"
                class="text-right"
                rowspan="2"
              >
                {{ col }}
              </th>
              <th class="text-right" rowspan="2">Total Revenue</th>
              <th class="text-right" rowspan="2">Total Paid</th>
              <th class="text-right" rowspan="2">Total Remaining</th>
            </tr>
            <tr>
              <th class="text-right">Male</th>
              <th class="text-right">Female</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in rows" :key="index">
              <td class="text-center font-weight-bold">
                {{ row.srNo || index + 1 }}
              </td>
              <td class="text-left font-weight-bold">{{ row.batchTitle }}</td>
              <td class="text-right">{{ formatCount(row.male) }}</td>
              <td class="text-right">{{ formatCount(row.female) }}</td>
              <td
                v-for="col in feeTypeColumns"
                :key="col"
                class="text-right"
              >
                {{ formatAmount(row.amounts && row.amounts[col]) }}
              </td>
              <td class="text-right font-weight-bold">
                {{ formatAmount(row.totalRevenue) }}
              </td>
              <td class="text-right">{{ formatAmount(row.totalPaid) }}</td>
              <td class="text-right">{{ formatAmount(row.totalRemaining) }}</td>
            </tr>
            <tr v-if="rows.length" class="totals-row">
              <td class="text-center font-weight-bold" colspan="2">Total</td>
              <td class="text-right font-weight-bold">
                {{ formatCount(totals.male) }}
              </td>
              <td class="text-right font-weight-bold">
                {{ formatCount(totals.female) }}
              </td>
              <td
                v-for="col in feeTypeColumns"
                :key="'total-' + col"
                class="text-right font-weight-bold"
              >
                {{ formatAmount(totals.amounts && totals.amounts[col]) }}
              </td>
              <td class="text-right font-weight-bold">
                {{ formatAmount(totals.totalRevenue) }}
              </td>
              <td class="text-right font-weight-bold">
                {{ formatAmount(totals.totalPaid) }}
              </td>
              <td class="text-right font-weight-bold">
                {{ formatAmount(totals.totalRemaining) }}
              </td>
            </tr>
            <tr v-if="!rows.length">
              <td :colspan="colSpan" class="text-center py-5 no-data">
                <feather-icon
                  icon="InboxIcon"
                  size="32"
                  class="text-muted mb-2"
                />
                <p>No fee revenue records found for the selected month</p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";
import {
  buildFeeRevenueRows,
  buildFeeRevenueTotals,
  feeRevenueDateRange,
  feeRevenueMonthLabel,
  formatFeeAmount,
  formatFeeCount,
} from "@/utils/feeRevenueReport";

export default {
  name: "FeeRevenuePrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      rows: [],
      feeTypeColumns: [],
      campusName: "MY SKOOL",
      campusBranch: "",
      schoolLogo: null,
      db: "",
      cID: "",
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
    };
  },
  computed: {
    periodLabel() {
      return feeRevenueMonthLabel(this.month, this.year) || this.currentDateTime;
    },
    currentDateTime() {
      const now = new Date();
      return now.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    totals() {
      return buildFeeRevenueTotals(this.rows, this.feeTypeColumns);
    },
    colSpan() {
      return 7 + this.feeTypeColumns.length;
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    formatAmount: formatFeeAmount,
    formatCount: formatFeeCount,
    initReport() {
      const query = this.$route.query;
      this.db = query.db || this.$store.state.userData.db;
      this.cID = query.cID || this.$store.state.userData.cId || 0;
      this.month = Number(query.month) || new Date().getMonth() + 1;
      this.year = Number(query.year) || new Date().getFullYear();

      if (!this.db) {
        this.error =
          "Database identifier is missing. Please close and try again.";
        return;
      }

      this.loadCampusInfo();
      this.loadReportData();
    },
    async loadCampusInfo() {
      try {
        const url = "https://apiv26.myskool.app/api/campuses";
        const response = await axios.get(url, {
          params: { db: this.db, cId: this.cID },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (
          response.data &&
          response.data.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find((c) => c.id == this.cID);
          if (match) {
            this.campusName = match.name;
            this.campusBranch = this.cID == 0 ? "All Campuses" : match.branch;
            this.schoolLogo = match.logo;
          }
        }
      } catch (e) {
        const branches = this.$store.state.branches || [];
        const currentBranch =
          branches.find((b) => b.id == this.cID) ||
          this.$store.state.currentBranch;
        if (currentBranch) {
          this.campusName =
            currentBranch.organizationName ||
            currentBranch.branch ||
            "MY SKOOL";
          this.campusBranch = currentBranch.branch || "";
        }
      }
    },
    async loadReportData() {
      this.loading = true;
      this.error = null;

      try {
        const payload = feeRevenueDateRange(this.month, this.year);
        const response = await axios.post(
          `${this.$store.state.domain}Fees/LoadFeeRevenueCollection?db=${this.db}&cID=${this.cID}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
              accept: "application/json",
              "content-type": "application/json",
            },
          }
        );

        let data = [];
        if (response.data && response.data.status === "success") {
          data = Array.isArray(response.data.data) ? response.data.data : [];
        } else if (Array.isArray(response.data)) {
          data = response.data;
        } else if (response.data && Array.isArray(response.data.data)) {
          data = response.data.data;
        } else {
          this.error =
            response.data?.message ||
            "The server returned an unexpected response format.";
          return;
        }

        const mapped = buildFeeRevenueRows(data);
        this.rows = mapped.rows;
        this.feeTypeColumns = mapped.feeTypeColumns;
      } catch (err) {
        console.error("Error fetching fee revenue report:", err);
        this.error =
          "Failed to retrieve report data. Please ensure the API endpoint is accessible.";
      } finally {
        this.loading = false;
      }
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.student-email-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: "Outfit", "Inter", sans-serif;
  color: #2d3436;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
    letter-spacing: -0.5px;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  span {
    margin-left: 4px;
  }

  &:active {
    transform: scale(0.96);
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;

  &:hover {
    background: linear-gradient(135deg, #5b4cc4 0%, #8c82eb 100%);
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.35);
    transform: translateY(-1px);
  }
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;

  &:hover {
    background: #fdfdfd;
    color: #e74c3c;
    border-color: #fab1a0;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }
}

.report-page {
  background: #ffffff;
  padding: 3rem 2.5rem;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  width: 98%;
  max-width: 1400px;
  margin: 30px auto;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.report-header-modern {
  border-bottom: 2px solid #333;
  padding-bottom: 20px;

  .school-logo-img {
    height: 130px;
    width: auto;
    object-fit: contain;
  }

  .school-name-full {
    font-size: 2rem;
    font-weight: 800;
    color: #000;
    text-transform: uppercase;
    letter-spacing: -0.5px;
  }

  .report-info-line {
    font-size: 1.1rem;
    color: #444;
    display: flex;
    align-items: center;

    .report-name-text {
      font-weight: 700;
      border-right: 2px solid #ddd;
      padding-right: 15px;
      margin-right: 15px;
    }

    .date-range-text {
      color: #555;
    }
  }
}

.ledger-style-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  border: 1px solid #dee2e6;
  background: white;

  th,
  td {
    border: 1px solid #dee2e6;
    padding: 10px 12px;
    box-sizing: border-box;
    font-size: 0.9rem;
  }

  thead {
    background: #2c3e50 !important;
    th {
      color: white !important;
      font-weight: 800;
      text-transform: uppercase;
      font-size: 0.78rem;
      letter-spacing: 0.4px;
      vertical-align: middle;
    }
  }

  tbody tr:nth-child(even) {
    background-color: #f8f9fa;
  }

  tbody tr:hover {
    background: #f1f4ff;
  }

  .totals-row td {
    background: #eef1f4 !important;
    font-weight: 800;
  }
}

@page {
  size: A4 landscape;
  margin: 0;
}

@media print {
  .no-print {
    display: none !important;
  }

  body {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
    color: #000 !important;
  }

  .student-email-report {
    background: white !important;
    padding: 0 !important;
  }

  .report-page {
    box-shadow: none !important;
    border: none !important;
    padding: 1cm 0.7cm !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
  }

  .ledger-style-table {
    font-size: 8.5pt !important;
    width: 100% !important;

    th,
    td {
      padding: 5px 7px !important;
      border: 1px solid #000 !important;
      color: #000 !important;
    }

    thead th {
      background: #f1f2f6 !important;
      color: #000 !important;
      font-weight: 800 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .totals-row td {
      background: #f1f2f6 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }

  .report-header-modern {
    margin-bottom: 18px !important;
    padding-bottom: 12px !important;
    border-bottom: 4px solid #000 !important;
    .school-logo-img {
      height: 110px !important;
      max-width: 280px !important;
      object-fit: contain !important;
    }
    .school-name-full {
      font-size: 1.5rem !important;
    }
  }
}
</style>
